import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { useGraphql } from "~/api-client";
import type {
  GetBaseMenusQuery,
  GetMenusQuery,
} from "~/graphql-client/generated";
import type { OrderDispatchOption } from "~/types";
import { useCart } from "./cart";
import { toEnumFulfillmentType } from "./menu-v2";

export interface MenuContextValue {
  locationRef: string | null;
  dispatchOption: OrderDispatchOption | null;
  fullOrdering: GetMenusQuery["ordering"];
  baseOrdering: GetBaseMenusQuery["ordering"];
  loadingFull: boolean;
  loadingBase: boolean;
}

const menuContext = createContext<MenuContextValue>({
  loadingFull: true,
  loadingBase: true,
  locationRef: null,
  dispatchOption: null,
  fullOrdering: null,
  baseOrdering: null,
});

export function useMenuContext() {
  return useContext(menuContext);
}

export function MenuProvider({
  locationRef,
  children,
}: {
  locationRef: string;
  children: ReactNode;
}) {
  const { dispatchOption } = useCart(locationRef);

  const { getBaseMenus, getMenus } = useGraphql();

  // base menu
  const { data: baseData, isLoading: loadingBase } = useQuery({
    queryKey: ["getBaseMenus", locationRef, dispatchOption],
    queryFn: () => {
      const enumType = toEnumFulfillmentType(dispatchOption || undefined);
      if (!enumType) return null;
      return getBaseMenus(locationRef, enumType);
    },
    refetchOnWindowFocus: false,
  });

  // full menu
  const { data: fullData, isLoading: loadingFull } = useQuery({
    queryKey: ["getMenus", locationRef, dispatchOption],
    queryFn: () => {
      const enumType = toEnumFulfillmentType(dispatchOption || undefined);
      if (!enumType) return null;
      return getMenus(locationRef, enumType);
    },
    refetchOnWindowFocus: false,
  });

  const fullOrdering = fullData?.ordering ?? null;
  const baseOrdering = baseData?.ordering ?? null;

  return (
    <menuContext.Provider
      value={{
        locationRef,
        dispatchOption,
        loadingBase,
        loadingFull,
        fullOrdering,
        baseOrdering,
      }}
    >
      {children}
    </menuContext.Provider>
  );
}
